import { AsyncAutocompleteField } from "@/features/FilterTable/AyncAutocompleteField";
import { DateField } from "@/features/FilterTable/DateField";
import {
  dateSchema,
  formatDateValueToISOString,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { getAuthorized, postClientAction } from "@/lib/fetch";
import { E3PdfPageSelection } from "@/pages/e3/E3PdfPageSelection/columns";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import {
  Button,
  Checkbox,
  CheckboxGroup,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@heroui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Resolver, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { useCurrentPageFromUrl } from "@/hooks/useClientActions";

const GeneratePdfSchema = z.object({
  client: z.preprocess(preprocessTextAsyncAutocompleteField, z.string()),
  reportingDate: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional()
    .nullable()
    .describe("date"),
  include_views: z.array(z.string()).optional(),
});

export type GeneratePdfSchemaType = z.infer<typeof GeneratePdfSchema>;

export default function GeneratePdf() {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPages, setSelectedPages] = useState<string[]>([]);

  const {
    handleSubmit,
    getValues,
    setValue,
    watch,
    register,
    formState: { isValid },
    reset,
  } = useForm<GeneratePdfSchemaType>({
    mode: "onChange",
    defaultValues: {
      client: "",
      reportingDate: undefined,
      include_views: [],
    },
    resolver: zodResolver(GeneratePdfSchema) as Resolver<GeneratePdfSchemaType>,
  });

  const clientValue = watch("client") as unknown as {
    id: number;
    text: string;
  };

  // Fetch PDF pages for the selected client
  const {
    data: pdfPagesResponse,
    isLoading: isPagesLoading,
    refetch,
  } = useQuery({
    queryKey: ["pdf-pages", clientValue],
    queryFn: async () => {
      if (!clientValue || !clientValue.text) return { count: 0, data: [] };

      const response = await getAuthorized(
        `${
          import.meta.env.VITE_BACKEND_URL
        }/e3/e3_pdf_page_selection?client_equal=${encodeURIComponent(
          clientValue.text,
        )}&page_size=1000`,
      );
      return response.json() as Promise<{
        count: number;
        data: E3PdfPageSelection[];
      }>;
    },
    refetchOnMount: false,
  });

  // Refetch PDF pages when client value changes
  useEffect(() => {
    if (clientValue && clientValue.text) {
      refetch();
    }
  }, [clientValue, refetch]);

  // Update selected pages when PDF pages data changes
  useEffect(() => {
    if (pdfPagesResponse?.data) {
      const defaultSelected = pdfPagesResponse.data
        .filter((page) => page.pdf_report_display === true && page.page_name)
        .map((page) => page.page_name!);
      setSelectedPages(defaultSelected);
      setValue("include_views", defaultSelected);
    }
  }, [pdfPagesResponse, setValue]);

  // Update form value when selected pages change
  useEffect(() => {
    setValue("include_views", selectedPages);
  }, [selectedPages, setValue]);

  const currentPage = useCurrentPageFromUrl();
  const generatePdfMutation = useMutation({
    mutationFn: async (data: GeneratePdfSchemaType) => {
      const formattedDate = formatDateValueToISOString(data.reportingDate);

      return await postClientAction(
        `${import.meta.env.VITE_BACKEND_URL}/e3/e3_download_center/generate`,
        {
          client: data.client,
          prefix: "e3",
          reportingDate: formattedDate ?? "",
          include_views: data.include_views,
          action_button_name: "Génération du rapport",
          action_execution_page: currentPage ?? "",
        },
      );
    },
    onSuccess: () => {
      toast.success("La génération du rapport PDF a été lancée.");
      setIsOpen(false);
      reset();
      setSelectedPages([]);
    },
    onError: () => {
      toast.error("Une erreur est survenue lors de la génération du rapport.");
    },
  });

  const handleFormSubmit = (data: GeneratePdfSchemaType) => {
    generatePdfMutation.mutate(data);
  };

  const handlePageSelectionChange = (pages: string[]) => {
    setSelectedPages(pages);
  };

  const pdfPages = pdfPagesResponse?.data || [];

  return (
    <Popover
      placement="bottom"
      showArrow
      offset={10}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    >
      <PopoverTrigger>
        <Button
          size="sm"
          radius="full"
          style={{
            border: "solid 2px transparent",
            backgroundImage: `linear-gradient(hsl(var(--heroui-background)), hsl(var(--heroui-background))), linear-gradient(83.87deg, #F54180, #9353D3)`,
            backgroundOrigin: "border-box",
            backgroundClip: "padding-box, border-box",
          }}
          endContent={<ChevronDownIcon className="h-4 w-4" />}
        >
          Génération du rapport
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        <form
          onSubmit={handleSubmit(handleFormSubmit)}
          className="space-y-4 p-1 w-full"
        >
          <div>
            <AsyncAutocompleteField<GeneratePdfSchemaType>
              name="client"
              label="Client"
              watch={watch}
              getValues={getValues}
              setValue={setValue}
              base_url="e3/e3_portfolios"
              column_name="client"
              id_column="client_id"
              showChip={false}
              keepDuplicates={false}
              required={true}
            />
          </div>

          <div>
            <DateField
              name="reportingDate"
              label="Date du rapport"
              watch={watch}
              setValue={setValue}
              register={register}
              required={true}
            />
          </div>

          {clientValue && clientValue.text && (
            <div className="space-y-2">
              <label className="text-sm text-primary">Pages à inclure</label>
              {isPagesLoading ? (
                <div className="text-sm text-gray-500">
                  Chargement des pages...
                </div>
              ) : pdfPages.length > 0 ? (
                <CheckboxGroup
                  value={selectedPages}
                  onValueChange={handlePageSelectionChange}
                  className="max-h-40 overflow-y-auto border rounded-lg p-2 overflow-x-hidden"
                >
                  {pdfPages.map((page) => (
                    <Checkbox
                      key={page.page_name}
                      value={page.page_name!}
                      className="text-sm"
                      size="sm"
                    >
                      {page.page_name}
                    </Checkbox>
                  ))}
                </CheckboxGroup>
              ) : (
                <div className="text-sm text-gray-500">
                  Aucune page disponible pour ce client
                </div>
              )}
            </div>
          )}

          <footer className="mt-4 w-full">
            <Button
              color="primary"
              radius="full"
              size="sm"
              type="submit"
              className="w-24"
              isDisabled={
                !isValid || (!!clientValue && selectedPages.length === 0)
              }
              isLoading={generatePdfMutation.isPending}
            >
              Générer
            </Button>
          </footer>
        </form>
      </PopoverContent>
    </Popover>
  );
}
